/**
 * SERVICE-ONELINE-01 (Seth 2026-09-17: "The recent service under 360 - I don't
 * like the notes that display. They are not helpful at getting an idea of what
 * those are. A more summarized / clean description and then we can click into
 * them for more info.")
 *
 * One headline per visit, shared by the Overview's Recent Service, the Service
 * tab and the History timeline:
 *   1. the AI line (`pegasus_visit_summary`, written by the summarize-visits
 *      edge function from the booking note + the tech's invoice notes — "what
 *      was done → the outcome"), cached per dispatch;
 *   2. until it lands: the rule-based `summarizeVisit()` when the invoice's
 *      work notes exist, else `cleanDispatchNote()` — the booking note with the
 *      window, phone numbers, call-ahead, timestamps and initials stripped.
 * The raw ESC note stays in the peek (and in the row's hover).
 *
 * HIST-AI (2026-06-16) built the first two pieces for the History tab; this
 * moves them here so every list reads the same line.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { CustomerDispatchRow } from "./api";

/** Prompt generation of the AI line — bump with the edge function; older rows regenerate on view. */
export const VISIT_SUMMARY_VERSION = 2;

/** The tech's work notes per dispatch, from the invoice lines (HIST-01). */
export function useCustomerWorkNotes(custno: string | undefined) {
  return useQuery({
    queryKey: ["work_notes", custno],
    enabled: !!custno,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Map<string, string>> => {
      const { data: sales } = await supabase
        .from("mirror_sales")
        .select("invoice, dispatch")
        .eq("custno", custno as string)
        .order("inv_date", { ascending: false })
        .limit(150);
      const inv2disp = new Map<string, string>();
      const ids: string[] = [];
      for (const s of (sales ?? []) as { invoice: string; dispatch: string | null }[]) {
        ids.push(s.invoice);
        if (s.dispatch) inv2disp.set(s.invoice, s.dispatch);
      }
      if (ids.length === 0) return new Map();
      const { data: lines } = await supabase
        .from("mirror_sales_line")
        .select("invoice, line_desc, ptype, line_count")
        .in("invoice", ids)
        .neq("ptype", "H") // H = HIST scheduling/call-log lines, not the work
        .order("line_count", { ascending: true })
        .limit(3000);
      const byInvoice = new Map<string, string[]>();
      for (const l of (lines ?? []) as { invoice: string; line_desc: string | null; ptype: string | null }[]) {
        const d = (l.line_desc ?? "").replace(/\r/g, "").trim();
        if (!d) continue;
        if (/^(payment received|sales tax|tax|balance forward|amount due|total)\b/i.test(d)) continue;
        if (/service agreement (reserve|reversal)/i.test(d) || /^reserve\s*\(minus\)/i.test(d)) continue; // membership accounting, not work
        if (/^(maintenance|service) agreement\b/i.test(d)) continue; // plan enrollment line, not visit work
        if ((l.ptype ?? "").toUpperCase() === "T") continue; // tax line
        if (!byInvoice.has(l.invoice)) byInvoice.set(l.invoice, []);
        byInvoice.get(l.invoice)!.push(d);
      }
      const byDispatch = new Map<string, string>();
      for (const [invoice, descs] of byInvoice) {
        const disp = inv2disp.get(invoice);
        if (!disp) continue;
        const text = descs.join("\n");
        byDispatch.set(disp, byDispatch.has(disp) ? `${byDispatch.get(disp)}\n${text}` : text);
      }
      return byDispatch;
    },
  });
}

/** What each visit billed (invoice totals per dispatch) — the quiet amount on the row. */
export function useCustomerInvoiceTotals(custno: string | undefined) {
  return useQuery({
    queryKey: ["invoice_totals_by_dispatch", custno],
    enabled: !!custno,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Map<string, number>> => {
      const { data } = await supabase
        .from("mirror_sales")
        .select("dispatch, inv_amount")
        .eq("custno", custno as string)
        .not("dispatch", "is", null)
        .limit(400);
      const out = new Map<string, number>();
      for (const s of (data ?? []) as { dispatch: string | null; inv_amount: number | null }[]) {
        if (!s.dispatch) continue;
        out.set(s.dispatch, (out.get(s.dispatch) ?? 0) + (Number(s.inv_amount) || 0));
      }
      return out;
    },
  });
}

/** The booking note with the dispatcher's plumbing stripped — window, "X of Y",
 *  call-ahead numbers, appended timestamped entries, initials, *asterisks*. */
export function cleanDispatchNote(notes: string | null | undefined, maxLen = 96): string {
  if (!notes) return "—";
  let s = notes.replace(/\r/g, " ").replace(/\s+/g, " ").trim();
  // Appended "9/3/2026 3:32:50 PM - MATT - …" entries are scheduling chatter; keep the head.
  s = s.split(/\s?\d{1,2}\/\d{1,2}\/\d{2,4},?\s+\d{1,2}:\d{2}(?::\d{2})?\s*[AP]M\s*-\s*[A-Z]{2,12}\s*-/)[0];
  s = s.replace(/^(8AM|8-12|10-2|12-4|3-7)[,\s]+/i, "");
  s = s.replace(/^\d+\s*of\s*\d+[,\s]+/i, "");
  s = s.replace(/^(?:\d{1,2}am\s*)?start[,\s]+/i, "");
  // "call 612-483-8462 on way" / "call owner John 651-295-9683 when on way" / "text … on the way"
  s = s.replace(/,?\s*(?:please\s+)?(?:call|text)\b[^,.;]*?\b(?:on|when)\s+(?:the\s+)?way\b[^,.;]*/gi, "");
  s = s.replace(/\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/g, "");
  s = s.replace(/\*([^*]+)\*/g, "$1");
  s = s.replace(/\s*-\s*[a-z]{2,4}\s*$/i, "");
  s = s.replace(/\s*,\s*(?:,\s*)+/g, ", ").replace(/\s+,/g, ",").replace(/^[,\s\-–·]+|[,\s\-–·]+$/g, "").trim();
  // Dispatcher shorthand → words the office reads at a glance.
  s = s.replace(/\bFRN\b/g, "furnace").replace(/\bCK\b/g, "check").replace(/\bHUMI\b/gi, "humidifier").replace(/\bT&M\b/g, "time & materials");
  if (!s) return "—";
  s = s.charAt(0).toUpperCase() + s.slice(1);
  if (s.length <= maxLen) return s;
  const sliced = s.slice(0, maxLen);
  const cut = Math.max(sliced.lastIndexOf(","), sliced.lastIndexOf(" "));
  return (cut > 24 ? sliced.slice(0, cut) : sliced).replace(/[,\s]+$/, "") + "…";
}

/** A high-level plain-English TITLE of a visit (Seth 2026-06-16) — the KIND of
 *  visit (tune-up / install / service call), the equipment, plan-vs-one-off,
 *  and a notable repair action — derived from the invoice work notes + the
 *  dispatch notes. Rule-based (instant, free); the AI line is the upgrade. */
export function summarizeVisit(work: string, dispatchNotes: string): string {
  const all = `${work}\n${dispatchNotes}`.toLowerCase();
  const eq: string[] = [];
  if (/\bfurnace\b|\bfrn\b|\bfurn\b/.test(all)) eq.push("Furnace");
  if (/\ba\/?c\b|air\s?condition|\bcondenser\b|cooling/.test(all)) eq.push("AC");
  if (/heat\s?pump/.test(all)) eq.push("Heat pump");
  if (/\bboiler\b/.test(all)) eq.push("Boiler");
  if (/water\s?heater/.test(all)) eq.push("Water heater");
  if (eq.length === 0 && /humidifier/.test(all)) eq.push("Humidifier");
  if (eq.length === 0 && /(spigot|faucet|drain|sump|plumb|toilet|water\s?line)/.test(all)) eq.push("Plumbing");
  const equip = eq.slice(0, 2).join(" & ");

  const plan = /\bplus\b|service\s?agreement|maintenance\s?agreement|\bmember\b|\d+\s*of\s*\d+/.test(all);
  const isTuneup = /tune.?up|\bck\b|precision tune|seasonal|clean(ed)?\s+and\s+check|cleaned (the )?(flame|burner|coil)/.test(all);
  const isInstall = !isTuneup && /install(ed|ation)?\b/.test(all) && /(furnace|condenser|coil|air handler|new system|heat pump|water heater)/.test(all);

  let kind = "service call";
  if (isTuneup) kind = "tune-up";
  else if (isInstall) kind = "install";

  // The NOTABLE thing that makes THIS visit findable in a list of routine ones:
  // a non-routine part replaced, or a recommendation made. Routine consumables
  // (filter/pad/panel) don't count — they're part of every tune-up.
  let notable = "";
  const repl = work.match(/replac(?:ed?|ing)\s+(?:the\s+|a\s+|an\s+)?([a-z][\w/ -]{2,26}?)(?=[.,;\n]|$)/i);
  const part = repl?.[1]?.trim().toLowerCase();
  if (part && !/\b(filter|media|pad|panel|batter(y|ies)|bulb|lamp)\b/.test(part)) {
    notable = `replaced ${part}`;
  } else if (/renewed|renewal/.test(all)) {
    notable = "renewed";
  } else {
    const rec = work.match(/recommend(?:ed|ing)?\s+(?:a\s+|the\s+|an\s+)?([a-z][\w/ -]{2,30}?)(?=[.,;\n]|$)/i);
    if (rec?.[1]) notable = `recommended ${rec[1].trim().toLowerCase()}`;
  }

  const head = `${equip ? equip + " " : ""}${kind}`;
  const planTag = isTuneup ? (plan ? " · PLUS plan" : " · one-off") : "";
  const notableTag = notable ? ` — ${notable}` : "";
  return (head.charAt(0).toUpperCase() + head.slice(1)) + notableTag + planTag;
}

type CachedSummary = { dispatch: string; title: string; prompt_version: number | null };

async function readSummaryCache(dispatches: string[], into: Map<string, string>) {
  if (dispatches.length === 0) return;
  const { data: cached } = await supabase
    .from("pegasus_visit_summary")
    .select("dispatch, title, prompt_version")
    .in("dispatch", dispatches);
  for (const c of (cached ?? []) as CachedSummary[]) {
    if ((c.prompt_version ?? 1) >= VISIT_SUMMARY_VERSION) into.set(c.dispatch, c.title);
  }
}

/** Visits being summarized RIGHT NOW, app-wide. TAB-SPEED-01 warms the
 *  Service and History tabs in the background while the Overview's five rows
 *  may still be at the model — two views asking for the same uncached visit
 *  share one edge call instead of paying for it twice. */
const inflight = new Map<string, Promise<void>>();

/** AI visit lines — cached in pegasus_visit_summary; missing or older-prompt
 *  rows are written by the summarize-visits edge function in batches of 25
 *  (up to four batches per view). Falls back to the rule-based title meanwhile. */
export function useVisitSummaries(custno: string | undefined, items: { dispatch: string; text: string }[]) {
  const key = items.map((i) => i.dispatch).sort().join(",");
  return useQuery({
    queryKey: ["visit_summaries", custno, key],
    enabled: !!custno && items.length > 0,
    staleTime: 24 * 60 * 60 * 1000,
    queryFn: async (): Promise<Map<string, string>> => {
      const map = new Map<string, string>();
      await readSummaryCache(items.map((i) => i.dispatch), map);
      const todo = items.filter((i) => !map.has(i.dispatch) && i.text.trim().length > 0).slice(0, 100);
      const theirs = todo.filter((i) => inflight.has(i.dispatch)).map((i) => i.dispatch);
      const mine = todo.filter((i) => !inflight.has(i.dispatch));
      for (let i = 0; i < mine.length; i += 25) {
        const chunk = mine.slice(i, i + 25);
        const run = (async () => {
          try {
            const { data } = await supabase.functions.invoke("summarize-visits", {
              body: { visits: chunk.map((v) => ({ id: v.dispatch, text: v.text })) },
            });
            const summaries = (data as { summaries?: Record<string, string> })?.summaries ?? {};
            for (const [dispatch, title] of Object.entries(summaries)) {
              if (typeof title === "string" && title.trim()) map.set(dispatch, title.trim());
            }
          } catch {
            /* fall back to the rule-based title */
          }
        })();
        for (const v of chunk) inflight.set(v.dispatch, run);
        try {
          await run;
        } finally {
          for (const v of chunk) inflight.delete(v.dispatch);
        }
      }
      if (theirs.length > 0) {
        // Another view is summarizing these — wait for it, then read what it wrote.
        await Promise.allSettled([...new Set(theirs.map((d) => inflight.get(d)).filter(Boolean))]);
        await readSummaryCache(theirs, map);
      }
      return map;
    },
  });
}

export interface VisitHeadline {
  text: string;
  source: "ai" | "rule" | "note";
  /** The raw booking note, for the hover / the peek. */
  raw: string;
  /** Invoice total for the visit, when one exists and is not zero. */
  amount: number | null;
}

/** One hook for a list of rows: AI lines for the visits that have work notes,
 *  the rule-based title or the cleaned booking note for the rest, the amount. */
export function useVisitHeadlines(custno: string | undefined, rows: CustomerDispatchRow[]) {
  const { data: workNotes } = useCustomerWorkNotes(custno);
  const { data: totals } = useCustomerInvoiceTotals(custno);
  const items = rows
    .filter((r) => !r.peg && workNotes?.get(r.dispatch))
    .map((r) => ({ dispatch: r.dispatch, text: `${workNotes!.get(r.dispatch)}\n${r.notes ?? ""}`.slice(0, 1400) }))
    .filter((v, i, arr) => arr.findIndex((x) => x.dispatch === v.dispatch) === i);
  const { data: ai } = useVisitSummaries(custno, items);
  const headlineFor = (r: CustomerDispatchRow): VisitHeadline => {
    const raw = (r.notes ?? "").replace(/\s+/g, " ").trim();
    const total = totals?.get(r.dispatch) ?? 0;
    const amount = Math.abs(total) > 0.005 ? total : null;
    const aiLine = ai?.get(r.dispatch);
    if (aiLine) return { text: aiLine, source: "ai", raw, amount };
    const work = workNotes?.get(r.dispatch);
    if (work) return { text: summarizeVisit(work, r.notes ?? ""), source: "rule", raw, amount };
    return { text: cleanDispatchNote(r.notes), source: "note", raw, amount };
  };
  return { headlineFor };
}
